/**
 * view.js — Impact Stat count-up animation
 *
 * Registered as the block's viewScript in block.json.
 * Runs only on the frontend; never loaded in the editor.
 *
 * Strategy:
 *  - IntersectionObserver fires once per block when it enters the viewport.
 *  - requestAnimationFrame drives the eased counter increment.
 *  - Integer stats use toLocaleString for thousands separators (1,000s).
 *  - Decimal stats preserve the same number of decimal places as the target
 *    throughout the animation (e.g. 1.09 stays 2 decimal places, not 1).
 */

/**
 * Easing function: ease-out cubic.
 * @param {number} t — progress 0–1
 * @returns {number}
 */
function easeOutCubic( t ) {
	return 1 - Math.pow( 1 - t, 3 );
}

/**
 * Count the decimal places in a number's string representation.
 *
 * @param {number} n
 * @returns {number}
 */
function decimalPlaces( n ) {
	const s   = n.toString();
	const dot = s.indexOf( '.' );
	return dot === -1 ? 0 : s.length - dot - 1;
}

/**
 * Format a number mid-animation, matching the final display format.
 *
 * @param {number} value    — current animated value
 * @param {number} decimals — decimal places in the target (0 = integer)
 * @returns {string}
 */
function formatValue( value, decimals ) {
	if ( decimals === 0 ) {
		return Math.round( value ).toLocaleString( 'en-US' );
	}
	// Preserve the same number of decimal places as the target
	// (e.g. 1.09 → 2 places; 35.5 → 1 place)
	return value.toFixed( decimals );
}

/**
 * Animate a single stat block.
 *
 * @param {HTMLElement} block
 */
function animateStat( block ) {
	const targetNumber  = parseFloat( block.dataset.statNumber  ?? 0 );
	const duration      = parseInt(   block.dataset.animationDuration ?? 1800, 10 );
	const decimals      = decimalPlaces( targetNumber );

	const numberEl = block.querySelector( '.impact-stat__number' );
	if ( ! numberEl ) return;

	let startTime = null;

	function step( timestamp ) {
		if ( ! startTime ) startTime = timestamp;

		const elapsed  = timestamp - startTime;
		const progress = Math.min( elapsed / duration, 1 );
		const eased    = easeOutCubic( progress );
		const current  = eased * targetNumber;

		numberEl.textContent = formatValue( current, decimals );

		if ( progress < 1 ) {
			requestAnimationFrame( step );
		} else {
			// Snap to exact final value (avoids floating-point drift)
			numberEl.textContent = formatValue( targetNumber, decimals );
		}
	}

	requestAnimationFrame( step );
}

// ── Observer setup ──────────────────────────────────────────────────────────

const blocks = document.querySelectorAll( '.wp-block-momentive-impact-stat' );

if ( blocks.length > 0 ) {
	// Respect prefers-reduced-motion, and the block's own "Count up" toggle
	// (off by choice for values like "2nd" or "#1", where counting from 0
	// doesn't make sense) — both skip animation and show the final value
	// immediately.
	const prefersReduced = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	const setFinalValue = ( block ) => {
		const targetNumber = parseFloat( block.dataset.statNumber ?? 0 );
		const decimals     = decimalPlaces( targetNumber );
		const numberEl     = block.querySelector( '.impact-stat__number' );
		if ( numberEl ) {
			numberEl.textContent = formatValue( targetNumber, decimals );
		}
	};

	// aria-label is a prohibited attribute on a <p> (generic/paragraph role has
	// no accessible name), which fails the axe "aria-prohibited-attr" audit.
	// The saved markup can't change without deprecating every stored instance,
	// so it's corrected here instead: drop the attribute and expose the final
	// value as visually hidden text. The animated prefix/number/suffix spans are
	// already aria-hidden, so assistive tech reads this once, never mid-count.
	const fixAccessibleName = ( block ) => {
		const valueEl = block.querySelector( '.impact-stat__value' );
		if ( ! valueEl || valueEl.querySelector( '.impact-stat__sr' ) ) return;
		const name = valueEl.getAttribute( 'aria-label' );
		valueEl.removeAttribute( 'aria-label' );
		if ( ! name ) return;
		const sr = document.createElement( 'span' );
		sr.className = 'impact-stat__sr sr-only';
		sr.textContent = name;
		valueEl.appendChild( sr );
	};

	const animatableBlocks = [];

	blocks.forEach( ( block ) => {
		fixAccessibleName( block );
		const shouldAnimate = ! prefersReduced && block.dataset.animate !== 'false';
		if ( shouldAnimate ) {
			animatableBlocks.push( block );
		} else {
			setFinalValue( block );
		}
	} );

	if ( animatableBlocks.length > 0 ) {
		const observer = new IntersectionObserver(
			( entries, obs ) => {
				entries.forEach( ( entry ) => {
					if ( entry.isIntersecting ) {
						animateStat( entry.target );
						obs.unobserve( entry.target ); // fire once only
					}
				} );
			},
			{
				threshold: 0.25, // trigger when 25% of the block is visible
			}
		);

		animatableBlocks.forEach( ( block ) => observer.observe( block ) );
	}
}
